import { Card } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Badge, Card as RSCard, CardBody, FormGroup } from "reactstrap";

import type { RootState } from "../../../redux/reducers/session/state";
import MonthlyStatsGraph from "./graphs/MonthlyStatsGraph";

const STARTING_YEAR = 2023,
  FINAL_YEAR = moment().format("YYYY");

interface MonthlyStatsDataItem {
  month: number;
  paidOutComms: number;
  uncollectedComms: number;
  activeReferrals: number;
}

interface MonthlyStatsInitialData {
  paidOutComms?: number;
  uncollectedComms?: number;
  activeReferrals?: number;
  perMonth?: MonthlyStatsDataItem[];
}

interface MonthlyStatsGraphCardProps {
  data?: Partial<MonthlyStatsInitialData>;
  isLoading: boolean;
  year: number;
  setYear: (year: number) => void;
}

const MonthlyStatsGraphCard = ({ data: initialData = {}, isLoading, year, setYear }: MonthlyStatsGraphCardProps) => {
  const { t } = useTranslation("referrer", { keyPrefix: "distributor.monthlyStatsGraph" });
  const _partnerSignUp = useSelector((state: RootState) => state.sessionState?.user?.createdAt);

  const years = useMemo(() => {
    // TODO: Use partnerSignUp for start year when ready
    // const startYear = moment(partnerSignUp).year();
    const startYear = 2024;
    const endYear = parseInt(FINAL_YEAR, 10);
    const yearsArray = [];
    for (let y = Math.max(startYear, STARTING_YEAR); y <= endYear; y++) {
      yearsArray.push(y);
    }
    return yearsArray;
  }, []);

  // Calculate totals from the data
  const { totalPaidOutComms, totalUncollectedComms, totalActiveReferrals } = useMemo(() => {
    if (!initialData) return { paidOutComms: 0, uncollectedComms: 0, activeReferrals: 0 };

    // Calculate total paid and uncollected commissions from the current year's data
    const totalPaidOutComms = initialData?.paidOutComms || 0;
    const totalUncollectedComms = initialData?.uncollectedComms || 0;

    // Get active referrals if available, or count from perMonth data
    const totalActiveReferrals = initialData?.activeReferrals || 0;

    return {
      totalPaidOutComms,
      totalUncollectedComms,
      totalActiveReferrals,
    };
  }, [initialData]);

  return (
    <RSCard>
      <Card.Header title={t("title")}>
        <FormGroup className="mb--4" style={{ minWidth: 120 }}>
          <FormInput.Select
            placeholder="Select a year"
            onChange={(opt) => setYear(opt?.value)}
            value={{ value: year, label: year }}
            options={years.map((y) => ({ value: y, label: y }))}
            isDisabled={isLoading}
            isClearable={false}
          />
        </FormGroup>
      </Card.Header>
      <CardBody className="pb-4">
        <div className="position-relative w-100 mb-4">
          <div className="position-absolute d-flex top-0 left-0 w-100" style={{ transform: "translateY(-50%)" }}>
            <Badge color="success" children={`${t("totalPaidOutLabel")}: ${totalPaidOutComms?.toFixed(2)} €`} />
            <Badge color="warning" children={`${t("totalUncollectedLabel")}: ${totalUncollectedComms?.toFixed(2)} €`} />
            <Badge color="info" children={`${t("activeReferrals")}: ${totalActiveReferrals}`} />
          </div>
        </div>

        <MonthlyStatsGraph
          year={year}
          data={initialData?.perMonth}
          isLoading={isLoading}
          className="rounded-bottom overflow-hidden"
          style={{ height: "calc(100vh - 400px)", minHeight: 400 }}
        />
      </CardBody>
    </RSCard>
  );
};

export default MonthlyStatsGraphCard;
